import { GetStaticProps } from "next";
import { NextSeo } from "next-seo";
import { Navbar } from "~/components/Navbar";
import { SidebarLayout } from "~/components/SidebarLayout";
import { SOCIAL_OG_IMAGE_URL } from "~/consts";
import { PageTrack } from "~/modules/analytics/components/PageTrack";
import { addBaseUrl } from "~/modules/app/seo/addBaseUrl";
import { AssetClassPageSEO } from "~/modules/app/seo/AssetClassPageSEO";
import { BreadcrumbSEO } from "~/modules/app/seo/BreadcrumbSEO";
import { UserFetcher } from "~/modules/user/components/UserFetcher";
import { NEXTJS_REVALIDATE_TIMEOUT } from "~/server/consts";
import { queryReferenceData } from "~/modules/reference_data/api/router";
import { DEFAULT_REFERENCE_DATA_LIST_QUERY } from "~/modules/reference_data/consts";
import {
    ReferenceDataColumns,
    type ReferenceData,
} from "~/modules/reference_data/types";
import { DataCatalogOverview } from "~/modules/catalog/pages/overview";
import { queryMeasures } from "~/modules/measures/api/router";
import { DEFAULT_MEASURE_LIST_QUERY } from "~/modules/measures/consts";
import { MeasureColumns, type Measure } from "~/modules/measures/types";
import { getTotalAssetCount } from "~/modules/assets/api/getAssetCount";
import { Breadcrumbs } from "~/components/Breadcrumbs";
import { FilterOperators } from "~/modules/filters/types";
import { Routes } from "~/routes";

// // // //

const SEO_TITLE = "Data Catalog";
const SEO_DESCRIPTION =
    "Explore the RWA.xyz data catalog — a comprehensive index of metrics and reference data fields used to analyze tokenized real-world assets.";

// // // //

interface Props {
    measures: Measure[];
    referenceData: ReferenceData[];
    totalAssetCount: number;
}

export default function DataCatalogLanding(props: Props) {
    const { measures, referenceData, totalAssetCount } = props;

    return (
        <PageTrack route={Routes.catalog}>
            <NextSeo
                title={SEO_TITLE}
                description={SEO_DESCRIPTION}
                canonical={addBaseUrl(Routes.catalog)}
                openGraph={{
                    type: "website",
                    url: addBaseUrl(Routes.catalog),
                    title: SEO_TITLE,
                    site_name: "RWA.xyz",
                    description: SEO_DESCRIPTION,
                    images: [
                        {
                            url: SOCIAL_OG_IMAGE_URL,
                            width: 4000,
                            height: 2300,
                            alt: "rwa.xyz",
                        },
                    ],
                }}
                additionalMetaTags={[
                    {
                        name: "keywords",
                        content:
                            "data catalog, RWA metrics, reference data, tokenized asset data, financial data dictionary, real world assets",
                    },
                ]}
            />
            <AssetClassPageSEO
                route={Routes.catalog}
                description={SEO_DESCRIPTION}
            />
            <BreadcrumbSEO
                items={[
                    {
                        name: SEO_TITLE,
                        href: Routes.catalog,
                    },
                ]}
            />
            <UserFetcher>
                {({ user }) => <Navbar route={Routes.catalog} user={user} />}
            </UserFetcher>
            <SidebarLayout pathname={Routes.catalog}>
                <Breadcrumbs
                    pages={[
                        {
                            name: SEO_TITLE,
                            current: true,
                        },
                    ]}
                />
                <div className="flex flex-col h-[calc(100vh-theme(spacing.breadcrumbs-height))] overflow-hidden">
                    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                        <DataCatalogOverview
                            measures={measures}
                            referenceData={referenceData}
                            totalAssetCount={totalAssetCount}
                        />
                    </div>
                </div>
            </SidebarLayout>
        </PageTrack>
    );
}

export const getStaticProps: GetStaticProps<Props> = async () => {
    const [measuresResponse, referenceDataResponse, totalAssetCountRes] =
        await Promise.all([
            queryMeasures({
                query: {
                    ...DEFAULT_MEASURE_LIST_QUERY,
                    filter: {
                        operator: FilterOperators.And,
                        filters: [
                            {
                                field: MeasureColumns.assetCount,
                                operator: FilterOperators.GreaterThan,
                                value: 0,
                            },
                            {
                                field: MeasureColumns.hidden,
                                operator: FilterOperators.Equals,
                                value: false,
                            },
                        ],
                    },
                },
            }),
            queryReferenceData({
                query: {
                    ...DEFAULT_REFERENCE_DATA_LIST_QUERY,
                    filter: {
                        operator: FilterOperators.And,
                        filters: [
                            {
                                field: ReferenceDataColumns.assetCount,
                                operator: FilterOperators.GreaterThan,
                                value: 0,
                            },
                            {
                                field: ReferenceDataColumns.hidden,
                                operator: FilterOperators.Equals,
                                value: false,
                            },
                        ],
                    },
                },
            }),
            getTotalAssetCount(),
        ]);

    const measures = (measuresResponse.results || []).filter(
        (m) => m.asset_count > 0,
    );
    const referenceData = referenceDataResponse.results || [];

    return {
        props: {
            measures,
            referenceData,
            totalAssetCount: totalAssetCountRes.count,
        },
        revalidate: NEXTJS_REVALIDATE_TIMEOUT,
    };
};
